import { Injectable } from "@angular/core";
import { IntiaroPublicApiClient } from "./api/intiaro-public-api-client.service";
import { ModularConfig, ModularConfigFactory } from "src/app/factories/modular-configuration-factory";
import { ListPrice, Price } from "src/app/data-models/price.model";
import { PimApiClient } from "src/app/services/api/pim-api-client.service";
import { CurrencyService} from "./currencyService";
import { RegionService} from "./region.service";
import { IPriceApiResponse, PriceFactory } from "../factories/price-factory";
import { firstValueFrom } from "rxjs";

export interface BodyForPricing {
  contexts: string[];
  region: string;
  brand: string;
  currency: string;
  configuration: any;
  version: string;
}

export interface IPricingResponse {
  price: {
    currency: string;
    value: number;
  }
}

export enum PimConfigurationKeyMapping {
  Default = "default_configuration",
  Custom = "product_configuration"
} 

@Injectable({
  providedIn: "root",
})
export class PriceCalculatorService {

  constructor(
    private intiaroPublicApiClient: IntiaroPublicApiClient,
    private pimApiClient: PimApiClient,
    private currencyService: CurrencyService,
    private regionService: RegionService,
  ) {}

  private async getPriceList(apiResponse: Promise<Response>, configurationKey: "default_configuration" | "product_configuration", pimTenantId: string, pimContext: string, productSystemVersion?: string): Promise<ListPrice> {
    try {
      const response = await apiResponse;
      const result = await response.json();
      const configuration = ModularConfigFactory.createFromBackendApi(result[configurationKey]);
      return await this.getPriceFromPim(configuration, pimContext, pimTenantId, productSystemVersion);
    } catch (_error) {
      return;
    }
  }
  
  public async getPriceFromPim(configuration: ModularConfig, context: string, pimTenantId: string, productSystemVersion: string, region?: string): Promise<ListPrice> {
    try {
      const response: IPricingResponse = await firstValueFrom(this.pimApiClient.calculatePrice(pimTenantId, [context], this.regionService.region, this.currencyService.currency, configuration, productSystemVersion));
      const price: Price = PriceFactory.createFromBackendApi(response as IPriceApiResponse);
      return price.listPrice;
    } catch (_error) {
      return;
    }
  }

  public async publicCalculate(configurationType: PimConfigurationKeyMapping, configurationId: string, pimTenantId: string, pimContext: string, productSystemVersion?: string): Promise<ListPrice> {
    const isDefault = configurationType === PimConfigurationKeyMapping.Default;
    const configurationDescriptionResponsePromise = isDefault
      ? this.intiaroPublicApiClient.getProductConfigForPricing(pimTenantId, configurationId)
      : this.intiaroPublicApiClient.getDesignConfigForPricing(configurationId);
    const configurationDescriptionKey: "default_configuration" | "product_configuration" = isDefault
      ? "default_configuration"
      : "product_configuration";

    return await this.getPriceList(
      configurationDescriptionResponsePromise,
      configurationDescriptionKey,
      pimTenantId,
      pimContext,
      productSystemVersion
    );
  }
}
